import { useState, useCallback } from "react";
import { getAllCustomers } from "../../../../services/customerApi";
import { useDropdownCache } from "../../../../shared/presentation/hooks/useDropdownCache";

export interface CustomerListItem {
  id: number;
  zoho_customerid: string;
  customer_id: string;
  customer_name: string;
  company_name: string;
  email: string;
  phone: string;
  status: string;
  billing_address: any;
  shipping_address: any;
  currency_code: string;
  currency_symbol: string;
  currency_id?: string;
  payment_terms: string;
  credit_limit: number;
  gst_treatment?: string;
  gstin_vat_tax_id?: string;
  country?: string;
  state?: string;
  created_at: string;
  updated_at: string;
}

export interface UseCustomersForDebitNoteReturn {
  customers: CustomerListItem[];
  loading: boolean;
  error: string | null;
  fetchCustomers: (
    searchTerm: string,
    limit: number,
    offset: number
  ) => Promise<{
    items: CustomerListItem[];
    hasMore?: boolean;
  }>;
  clearError: () => void;
  reset: () => void;
}

/**
 * Hook for fetching customers with pagination and caching
 * Supports SearchableDropdown fetchItems function signature
 */
export const useCustomersForDebitNote =
  (): UseCustomersForDebitNoteReturn => {
    const [customers, setCustomers] = useState<CustomerListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { getCached, setCached } = useDropdownCache();

    /**
     * Fetch customers with pagination and caching
     * Supports SearchableDropdown fetchItems function signature
     */
    const fetchCustomers = useCallback(
      async (
        searchTerm: string,
        limit: number,
        offset: number
      ): Promise<{
        items: CustomerListItem[];
        hasMore?: boolean;
      }> => {
        // Only cache the initial load (offset 0, no search)
        const shouldCache = offset === 0 && !searchTerm;
        const cacheKey = { searchTerm, limit, offset };

        if (shouldCache) {
          const cached = getCached<{
            items: CustomerListItem[];
            hasMore?: boolean;
          }>("customers", cacheKey);

          if (cached) {
            return cached;
          }
        }

        setLoading(true);
        setError(null);

        try {
          const response = await getAllCustomers({
            search: searchTerm || undefined,
            limit,
            offset,
          });

          // Map response to CustomerListItem format
          const items: CustomerListItem[] = (response.data || []).map(
            (customer: any) => ({
              id: customer.id || 0,
              zoho_customerid:
                customer.zoho_customerid || customer.customer_id || "",
              customer_id: customer.customer_id || "",
              customer_name: customer.customer_name || "",
              company_name:
                customer.company_name || customer.customer_name || "",
              email: customer.email || customer.contact_email_1 || "",
              phone: customer.phone || customer.contact_number_1 || "",
              status: customer.status || "active",
              billing_address: customer.billing_address || null,
              shipping_address: customer.shipping_address || null,
              currency_code:
                customer.currency_code || customer.currency_id || "",
              currency_symbol: customer.currency_symbol ?? "",
              currency_id: customer.currency_id || "",
              payment_terms: customer.payment_terms || "",
              credit_limit: customer.credit_limit || 0,
              gst_treatment: customer.gst_treatment || "",
              gstin_vat_tax_id: customer.gstin_vat_tax_id || "",
              country: customer.country || "",
              state: customer.state || "",
              created_at: customer.created_at || new Date().toISOString(),
              updated_at: customer.updated_at || new Date().toISOString(),
            })
          );

          const hasMore = response.pagination?.has_more || false;

          const result = {
            items,
            hasMore,
          };

          // Cache the result if it's the initial load
          if (shouldCache) {
            setCached("customers", cacheKey, result);
          }

          return result;
        } catch (error: any) {
          const errorMessage = error.message || "Failed to fetch customers";
          setError(errorMessage);
          console.error("Failed to fetch customers:", error);
          return {
            items: [],
            hasMore: false,
          };
        } finally {
          setLoading(false);
        }
      },
      [getCached, setCached]
    );

    /**
     * Clear error
     */
    const clearError = useCallback((): void => {
      setError(null);
    }, []);

    /**
     * Reset state
     */
    const reset = useCallback((): void => {
      setCustomers([]);
      setError(null);
      setLoading(false);
    }, []);

    return {
      customers,
      loading,
      error,
      fetchCustomers,
      clearError,
      reset,
    };
  };

